import type { FunctionComponent, CSSProperties } from 'react';

import { ActionTableSortType } from 'models';
import { Column, SortParams } from './models';

import MuiTable from '@material-ui/core/Table';
import TableHead from './action-table-head';
import MuiTableBody from '@material-ui/core/TableBody';
import ActionTableRow from './action-table-row';

import ActionTableCheckbox from './action-table-checkbox';

interface ActionTableProps<
  Data extends Record<string, any> = any,
  Component extends FunctionComponent = typeof ActionTableCheckbox
> {
  className?: string;
  data: Data[];
  columns: Column<Data>[];
  HeadProps?: {
    withCheckbox?: boolean;
    CheckboxComponent?: Component;
    CheckboxComponentProps?: Component['defaultProps'];
  };
  BodyProps?:
    | ((data: Data) => {
        withCheckbox?: boolean;
        CheckboxComponent?: Component;
        CheckboxComponentProps?: Component['defaultProps'];
      })
    | {
        withCheckbox?: boolean;
        CheckboxComponent?: Component;
        CheckboxComponentProps?: Component['defaultProps'];
      };
  withSort?: boolean;
  sortParams?: SortParams;
  onRowClick?: (data: Data, index: number) => void;
  onChangeSortParams?: (orderBy: string, sortType: ActionTableSortType) => void;
}

const DefaultCheckboxComponent = ActionTableCheckbox;
const DefaultCheckboxProps = ActionTableCheckbox.defaultProps;

const ActionTable: React.FC<ActionTableProps> = (props) => {
  const {
    className,
    columns,
    data,
    HeadProps = {
      withCheckbox: false,
      CheckboxComponent: DefaultCheckboxComponent,
      CheckboxComponentProps: DefaultCheckboxProps,
    },
    BodyProps = {
      withCheckbox: false,
      CheckboxComponent: DefaultCheckboxComponent,
      CheckboxComponentProps: DefaultCheckboxProps,
    },
    withSort = false,
    sortParams,
    onChangeSortParams,
    onRowClick,
  } = props;
  const {
    withCheckbox: withHeadCheckbox = false,
    CheckboxComponent: HeadCheckboxComponent = DefaultCheckboxComponent,
    CheckboxComponentProps: HeadCheckboxComponentProps = DefaultCheckboxProps,
  } = HeadProps;

  return (
    <MuiTable className={className}>
      <TableHead
        columns={columns}
        withCheckbox={withHeadCheckbox}
        withSort={withSort}
        sortParams={sortParams}
        onChangeSortParams={onChangeSortParams}
        CheckboxComponent={HeadCheckboxComponent}
        CheckboxComponentProps={HeadCheckboxComponentProps}
      />
      <MuiTableBody>
        {data.map((el, i) => {
          let {
            withCheckbox = false,
            CheckboxComponent = DefaultCheckboxComponent,
            CheckboxComponentProps = DefaultCheckboxProps,
          } = BodyProps
            ? typeof BodyProps === 'function'
              ? BodyProps(el)
              : BodyProps
            : {
                withCheckbox: false,
                CheckboxComponent: DefaultCheckboxComponent,
                CheckboxComponentProps: DefaultCheckboxProps,
              };

          return (
            <ActionTableRow
              key={i}
              data={el}
              columns={columns}
              withCheckbox={withCheckbox}
              CheckboxComponent={CheckboxComponent}
              CheckboxComponentProps={CheckboxComponentProps}
              onClick={onRowClick ? () => onRowClick(el, i) : undefined}
            />
          );
        })}
      </MuiTableBody>
    </MuiTable>
  );
};

export type ActionTableColumn<
  T extends Record<string, any> = Record<string, any>
> = Column<T>;
export type ActionTableSortParams = SortParams;
export default ActionTable;
